"use client";

import { useState, useRef, useEffect } from "react";
import { Bot, X, Send } from "lucide-react";
import { useT } from "@/context/I18nContext";
import { usePWA } from "@/utlis/usePWA";
import { useContextElement } from "@/context/Context";

const LINK_PATTERN = /(\bhttps?:\/\/[^\s]+|\/[a-zA-Z][a-zA-Z0-9\-\/#]*|\b[\w.-]+@[\w.-]+\.\w+\b)/g;

function LinkifiedText({ text }) {
	const parts = String(text || "").split(LINK_PATTERN);

	return parts.map((part, i) => {
		if (!part) return null;
		if (/^https?:\/\//.test(part)) {
			return (
				<a key={i} href={part} target='_blank' rel='noopener noreferrer'>
					{part}
				</a>
			);
		}
		if (/^\/[a-zA-Z]/.test(part)) {
			return (
				<a key={i} href={part}>
					{part}
				</a>
			);
		}
		if (/^[\w.-]+@[\w.-]+\.\w+$/.test(part)) {
			return (
				<a key={i} href={`mailto:${part}`}>
					{part}
				</a>
			);
		}
		return <span key={i}>{part}</span>;
	});
}

export default function ChatWidget() {
	const isPWA = usePWA();
	const { t, locale } = useT();
	const { chatOpen, setChatOpen } = useContextElement();
	const [localOpen, setLocalOpen] = useState(false);
	const open = isPWA ? chatOpen : localOpen;
	const setOpen = isPWA ? setChatOpen : setLocalOpen;
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const bottomRef = useRef(null);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, loading]);

	const quickLinks = [
		{ label: t("contact"), href: "/contact" },
		{ label: t("formular_retur"), href: "/formular-retur" },
		{ label: t("14_zile_retur"), href: locale === "ro" ? "/termeni-si-conditii#14" : `/termeni-si-conditii/${locale}#14` },
	];

	const sendMessage = async (presetText) => {
		const text = (presetText ?? input).trim();
		if (!text || loading) return;

		const nextMessages = [...messages, { role: "user", content: text }];
		setMessages(nextMessages);
		setInput("");
		setLoading(true);

		try {
			const res = await fetch("/api/chat", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					message: text,
					locale,
					history: nextMessages.slice(-6),
				}),
			});
			const data = await res.json();

			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content: data.reply || t("chat_error"),
					products: data.products || [],
				},
			]);
		} catch {
			setMessages((prev) => [...prev, { role: "assistant", content: t("chat_error"), products: [] }]);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	return (
		<>
			{!isPWA && (
				<button className='chat-launcher' onClick={() => setOpen((v) => !v)} aria-label={t("chat_title")}>
					{!open && <span className='chat-launcher-ring' />}
					{open ? <X size={24} /> : <Bot size={26} />}
				</button>
			)}

			{open && (
				<div className={`chat-panel${isPWA ? " pwa" : ""}`}>
					<div className='chat-header'>
						<span className='chat-header-title'>
							<Bot size={18} />
							{t("chat_title")}
						</span>
						<button className='chat-close-btn' onClick={() => setOpen(false)} aria-label='close'>
							<X size={18} />
						</button>
					</div>

					<div className='chat-body'>
						{messages.length === 0 && (
							<>
								<div className='chat-empty'>{t("chat_placeholder_greeting")}</div>
								<div className='chat-quicklinks'>
									{quickLinks.map((link) => (
										<a key={link.href} href={link.href} className='chat-quicklink'>
											{link.label}
										</a>
									))}
								</div>
							</>
						)}

						{messages.map((m, i) => (
							<div key={i} className={`chat-bubble ${m.role}`}>
								{m.role === "assistant" && (
									<div className='chat-avatar'>
										<Bot size={14} />
									</div>
								)}
								<div className='chat-bubble-content'>
									<div className='chat-bubble-text'>
										<LinkifiedText text={m.content} />
									</div>

									{m.products?.length > 0 && (
										<div className='chat-products'>
											{m.products.map((p) => (
												<a key={p.id} href={p.url || "#"} className='chat-product-card'>
													{p.image && <img src={p.image} alt={p.name} />}
													<div className='chat-product-info'>
														<span className='chat-product-name'>{p.name}</span>
														{p.price && (
															<span className='chat-product-price'>
																{p.priceOriginal && (
																	<span className='chat-product-price-old'>
																		{p.priceOriginal.amount} {p.priceOriginal.currency}
																	</span>
																)}
																{p.price.amount} {p.price.currency}
															</span>
														)}
													</div>
												</a>
											))}
										</div>
									)}
								</div>
							</div>
						))}

						{loading && (
							<div className='chat-bubble assistant'>
								<div className='chat-avatar'>
									<Bot size={14} />
								</div>
								<div className='chat-typing'>
									<span />
									<span />
									<span />
								</div>
							</div>
						)}

						<div ref={bottomRef} />
					</div>

					<div className='chat-input-row'>
						<input
							type='text'
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={t("chat_input_placeholder")}
							maxLength={500}
						/>
						<button onClick={() => sendMessage()} disabled={loading || !input.trim()} aria-label={t("chat_send")}>
							<Send size={18} />
						</button>
					</div>
				</div>
			)}

			<style jsx>{`
				.chat-launcher {
					position: fixed;
					bottom: 24px;
					right: 24px;
					width: 56px;
					height: 56px;
					border-radius: 50%;
					background: #0074d9;
					color: #fff;
					border: none;
					display: flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
					cursor: pointer;
					z-index: 9998;
				}

				@media (max-width: 991px) {
					.chat-launcher {
						bottom: 14px;
					}
					:global(body.mobile-menu-open) .chat-launcher,
					:global(body.addr-sheet-open) .chat-launcher {
						display: none;
					}
				}

				.chat-launcher-ring {
					position: absolute;
					inset: 0;
					border-radius: 50%;
					box-shadow: 0 0 0 0 rgba(0, 116, 217, 0.55);
					animation: chat-pulse 2.4s infinite;
					pointer-events: none;
				}

				@keyframes chat-pulse {
					0% {
						box-shadow: 0 0 0 0 rgba(0, 116, 217, 0.55);
					}
					70% {
						box-shadow: 0 0 0 12px rgba(0, 116, 217, 0);
					}
					100% {
						box-shadow: 0 0 0 0 rgba(0, 116, 217, 0);
					}
				}

				.chat-panel {
					position: fixed;
					bottom: 92px;
					right: 24px;
					width: min(360px, calc(100vw - 32px));
					height: min(500px, calc(100vh - 140px));
					background: #fff;
					border-radius: 14px;
					box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
					display: flex;
					flex-direction: column;
					overflow: hidden;
					z-index: 9998;
				}

				.chat-panel.pwa {
					bottom: calc(env(safe-area-inset-bottom) + 14px + 68px + 10px);
					right: 16px;
					width: min(360px, calc(100vw - 32px));
					height: min(500px, calc(100vh - 220px));
				}

				.chat-header {
					background: #0074d9;
					color: #fff;
					padding: 12px 12px 12px 16px;
					font-weight: 600;
					font-size: 14px;
					display: flex;
					align-items: center;
					justify-content: space-between;
				}

				.chat-header-title {
					display: flex;
					align-items: center;
					gap: 8px;
				}

				.chat-close-btn {
					background: none;
					border: none;
					color: #fff;
					opacity: 0.85;
					cursor: pointer;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 4px;
				}

				.chat-close-btn:hover {
					opacity: 1;
				}

				.chat-body {
					flex: 1;
					overflow-y: auto;
					padding: 14px;
					display: flex;
					flex-direction: column;
					gap: 10px;
					background: #f7f9fb;
				}

				.chat-empty {
					color: #777;
					font-size: 13px;
					text-align: center;
					margin-top: 12px;
				}

				.chat-quicklinks {
					display: flex;
					flex-direction: column;
					gap: 6px;
					margin-top: 12px;
				}

				.chat-quicklink {
					background: #fff;
					border: 1px solid #dfe3e8;
					border-radius: 8px;
					padding: 8px 12px;
					font-size: 12.5px;
					font-weight: 500;
					color: #0074d9;
					text-decoration: none;
					text-align: center;
				}

				.chat-quicklink:hover {
					background: #eef6ff;
				}

				.chat-bubble {
					max-width: 90%;
					display: flex;
					align-items: flex-start;
					gap: 6px;
				}

				.chat-bubble.user {
					align-self: flex-end;
				}

				.chat-bubble.assistant {
					align-self: flex-start;
				}

				.chat-avatar {
					width: 22px;
					height: 22px;
					border-radius: 50%;
					background: #0074d9;
					color: #fff;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-shrink: 0;
					margin-top: 2px;
				}

				.chat-bubble-content {
					display: flex;
					flex-direction: column;
					gap: 8px;
					min-width: 0;
				}

				.chat-bubble-text {
					padding: 9px 12px;
					border-radius: 12px;
					font-size: 13.5px;
					line-height: 1.4;
					white-space: pre-wrap;
				}

				.chat-bubble-text a {
					color: inherit;
					text-decoration: underline;
					font-weight: 600;
				}

				.chat-bubble.user .chat-bubble-text {
					background: #0074d9;
					color: #fff;
					border-bottom-right-radius: 3px;
				}

				.chat-bubble.assistant .chat-bubble-text {
					background: #fff;
					color: #222;
					border: 1px solid #e4e8ee;
					border-bottom-left-radius: 3px;
				}

				.chat-bubble.assistant .chat-bubble-text a {
					color: #0074d9;
				}

				.chat-typing {
					display: flex;
					gap: 4px;
					padding: 12px;
					background: #fff;
					border: 1px solid #e4e8ee;
					border-radius: 12px;
					width: fit-content;
				}

				.chat-typing span {
					width: 6px;
					height: 6px;
					border-radius: 50%;
					background: #aaa;
					animation: chat-typing-blink 1.2s infinite ease-in-out;
				}

				.chat-typing span:nth-child(2) {
					animation-delay: 0.2s;
				}

				.chat-typing span:nth-child(3) {
					animation-delay: 0.4s;
				}

				@keyframes chat-typing-blink {
					0%,
					80%,
					100% {
						opacity: 0.3;
					}
					40% {
						opacity: 1;
					}
				}

				.chat-products {
					display: flex;
					flex-direction: column;
					gap: 8px;
				}

				.chat-product-card {
					display: flex;
					gap: 10px;
					background: #fff;
					border: 1px solid #e4e8ee;
					border-radius: 10px;
					padding: 8px;
					text-decoration: none;
					color: inherit;
				}

				.chat-product-card img {
					width: 52px;
					height: 52px;
					object-fit: cover;
					border-radius: 8px;
					flex-shrink: 0;
				}

				.chat-product-info {
					display: flex;
					flex-direction: column;
					justify-content: center;
					gap: 3px;
					min-width: 0;
				}

				.chat-product-name {
					font-size: 12.5px;
					font-weight: 600;
					color: #222;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.chat-product-price {
					font-size: 13px;
					font-weight: 700;
					color: #0074d9;
					display: flex;
					gap: 6px;
					align-items: center;
				}

				.chat-product-price-old {
					font-size: 11px;
					font-weight: 400;
					color: #999;
					text-decoration: line-through;
				}

				.chat-input-row {
					display: flex;
					gap: 8px;
					padding: 10px;
					border-top: 1px solid #e4e8ee;
				}

				.chat-input-row input {
					flex: 1;
					border: 1px solid #dfe3e8;
					border-radius: 20px;
					padding: 8px 14px;
					font-size: 13px;
					outline: none;
				}

				.chat-input-row button {
					width: 36px;
					height: 36px;
					border-radius: 50%;
					background: #0074d9;
					color: #fff;
					border: none;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					flex-shrink: 0;
				}

				.chat-input-row button:disabled {
					opacity: 0.5;
					cursor: default;
				}

				.chat-disclaimer {
					font-size: 10px;
					color: #999;
					text-align: center;
					padding: 4px 10px 8px;
				}
			`}</style>
		</>
	);
}
